/**
 * **L'info-bulle de découverte, non bloquante** — ⟦A-129 · C11⟧.
 *
 * Le pendant de `ModeDecouverte` pour les leçons `bloquante: false` (`game/decouverte.ts`) :
 * énergie, veille, fil, image, tendances, arbre, réputation, fidélité, momentum, longue, temps,
 * argent. Toutes déclenchées par les MÊMES conditions `quand()` qu'avant — seul l'écran change.
 *
 * Trois différences avec le voile bloquant, et elles portent tout le sens du chantier :
 *
 * 1. **Pas de voile opaque.** Une petite fiche, ancrée sur l'élément concerné
 *    (`data-decouverte-ancre="…"`, posé sur l'écran) — ou en position par défaut si l'ancre
 *    n'est pas à l'écran en ce moment. Une ancre absente ne bloque jamais et ne plante jamais :
 *    c'est un repli, pas une erreur.
 * 2. **Le jeu continue derrière.** Rien n'intercepte les clics du jeu : la fiche porte
 *    `pointer-events: none`, et c'est un écouteur global (`pointerdown`, phase de capture, sans
 *    `preventDefault`) qui referme la bulle — le clic continue ensuite son chemin normal vers
 *    ce qu'il visait vraiment. « Fermée d'un clic n'importe où » veut dire ÇA : même un clic
 *    sur un bouton du jeu la referme, et ce même clic agit quand même.
 * 3. **Pas de règle « une par tour ».** Une seule à l'écran à la fois (`couranteBulle`) suffit :
 *    rien n'empêche la suivante de s'afficher aussitôt la précédente refermée, y compris dans
 *    le même tour, puisque le jeu, lui, n'a jamais été mis en pause.
 *
 * ## ⟦C57 · lot 3 · A-174⟧ Le chapeau et « À retenir » en UNE phrase, rien d'autre
 *
 * La bulle pesait une soixantaine de mots à côté d'un jeu qui continue : on ne lit plus rien. Elle garde le
 * titre (5 mots au plus), le chapeau et « À retenir » en UNE phrase de 12 mots au plus (`a_retenir_court`).
 * **Pas de bouton « Les dessous » ici, et c'est voulu** : la bulle est `pointer-events: none` et se referme
 * au moindre clic, un bouton y serait mangé avant d'ouvrir quoi que ce soit. Les paragraphes d'avant restent
 * intacts dans `game/decouverte.ts` (`corps`, `paragraphes()`), et le voile bloquant (`ModeDecouverte`) les
 * ouvre derrière « Les dessous ».
 */

import { useEffect, useLayoutEffect, useState, type CSSProperties } from "react";

import { aRetenirCourtDe, modaleEvenementOuverte, titreCourtDe } from "../game/decouverte";
import { infoBulleDue, useDecouverteStore } from "../stores/decouverteStore";
import { useRunStore } from "../stores/runStore";
import { useUIStore } from "../stores/uiStore";
import { Fenetre } from "./Fenetre";
import { useSfx } from "./SoundProvider";

interface Position {
  top: number;
  left: number;
  /** L'ancre a été trouvée à l'écran : on peut orienter la petite flèche vers elle. */
  ancree: boolean;
}

/** Position par défaut — bas-gauche, au-dessus de la barre d'actions : jamais un centre d'écran
 *  qui masquerait le jeu, jamais un plantage si l'ancre demandée n'existe pas ici. */
const POSITION_DEFAUT: Position = { top: -1, left: -1, ancree: false };

function positionDe(ancre: string | undefined): Position {
  if (!ancre) return POSITION_DEFAUT;
  const el = document.querySelector(`[data-decouverte-ancre="${ancre}"]`);
  if (!el) return POSITION_DEFAUT;
  const r = el.getBoundingClientRect();
  // Sous l'élément, calé à gauche sur lui — puis repoussée pour rester dans le viewport.
  const largeurBulle = 320;
  const left = Math.min(Math.max(8, r.left), window.innerWidth - largeurBulle - 8);
  const top = Math.min(r.bottom + 10, window.innerHeight - 120);
  return { top, left, ancree: true };
}

export function InfoBulleDecouverte() {
  const run = useRunStore((s) => s.run);
  const state = run?.state ?? null;
  const runId = run?.runId ?? null;

  const actif = useUIStore((s) => s.settings.decouverte);
  const dialogue = useUIStore((s) => s.dialog);
  const momentsSuspendus = useUIStore((s) => s.momentsSuspendus);
  // ⟦C16⟧ L'événement (bloquant) ou une offre dépliée est une modale de jeu comme les autres —
  // « la pop-up qui expliquait la communauté pendant qu'il y avait la pop-up qui faisait le
  // premier événement […] tu perds les infos » (Mathieu). Ni l'un ni l'autre n'étaient dans
  // `libre` avant ce chantier : rien n'empêchait une info-bulle de s'afficher PAR-DESSUS.
  const offreOuverte = useUIStore((s) => s.offreOuverte);
  const modaleOuverte = modaleEvenementOuverte(state?.pending_event ?? null, offreOuverte);
  const paliers = useRunStore((s) => s.paliersEnAttente.length);
  const chute = useRunStore((s) => s.chute);

  // Un voile BLOQUANT passe devant : deux fiches en même temps sur le même écran, ce serait de
  // nouveau l'embouteillage que C11 corrige.
  const courante = useDecouverteStore((s) => s.courante);

  const couranteBulle = useDecouverteStore((s) => s.couranteBulle);
  const vues = useDecouverteStore((s) => s.vues);
  const synchroniser = useDecouverteStore((s) => s.synchroniser);
  const demanderBulle = useDecouverteStore((s) => s.demanderBulle);
  const fermerBulle = useDecouverteStore((s) => s.fermerBulle);

  const sfx = useSfx();
  const [position, setPosition] = useState<Position>(POSITION_DEFAUT);

  useEffect(() => {
    if (runId) synchroniser(runId);
  }, [runId, synchroniser]);

  // ⚠️ Ne dépend PAS de `tourDeLaDerniere` ni du tour : contrairement au voile bloquant, une
  // info-bulle n'a pas de limite « une par tour ». Elle attend seulement son tour d'affichage
  // (une seule à la fois) et qu'aucun autre moment de jeu n'occupe l'écran.
  const libre =
    !courante && !couranteBulle && !dialogue && !momentsSuspendus && !modaleOuverte && paliers === 0 && chute === null;

  useEffect(() => {
    if (!actif || !state || !libre) return;
    const due = infoBulleDue(state);
    if (due) demanderBulle(due.id);
  }, [actif, state, libre, vues, demanderBulle]);

  useLayoutEffect(() => {
    if (!couranteBulle) return;
    setPosition(positionDe(couranteBulle.ancre));
  }, [couranteBulle]);

  useEffect(() => {
    // ⟦C16⟧ `modaleOuverte` en plus de `couranteBulle` : sinon un clic sur un bouton de
    // l'événement (pendant que la bulle est CACHÉE par le garde de rendu ci-dessous) fermerait
    // quand même la bulle — et la marquerait vue pour le reste de la run — sans qu'elle ait
    // jamais été visible. Tant que la modale est ouverte, ce clic ne doit lui appartenir qu'à
    // elle.
    if (!couranteBulle || modaleOuverte) return;
    sfx("dialog_open");
    // Capture, et surtout PAS de `preventDefault`/`stopPropagation` : le clic doit continuer
    // son chemin normal vers ce qu'il visait — c'est ce qui fait que « le jeu continue
    // derrière ». `pointerdown` plutôt que `click` : ça referme dès l'appui, pas au relâché.
    const fermerAuProchainClic = () => fermerBulle();
    document.addEventListener("pointerdown", fermerAuProchainClic, true);
    return () => document.removeEventListener("pointerdown", fermerAuProchainClic, true);
  }, [couranteBulle, modaleOuverte, fermerBulle, sfx]);

  // ⟦C16⟧ Filet de sécurité, en plus de `libre` ci-dessus : si un événement/une offre s'ouvre
  // pendant qu'une info-bulle est DÉJÀ affichée (course rare — un clic ferme normalement la
  // bulle avant que l'action qu'il déclenche ne pose un `pending_event`), on la CACHE sans la
  // fermer. `fermerBulle` la marquerait vue pour le reste de la run — la cacher la laisse
  // due, donc elle reprend sa place dès que la modale se ferme (`libre` redevient vrai).
  if (!couranteBulle || modaleOuverte) return null;

  const style: CSSProperties = position.ancree
    ? { top: position.top, left: position.left }
    : { left: "50%", bottom: "18%", transform: "translateX(-50%)" };

  return (
    <aside
      className={`decouverte-bulle${position.ancree ? " decouverte-bulle-ancree" : ""}`}
      role="status"
      aria-live="polite"
      style={style}
    >
      {/* ⟦C48 · A-168 §3⟧ L'info-bulle est une petite `Fenetre` (titre et icône dans le bandeau) : c'était
          une carte crème penchée à coins arrondis. Toujours sans croix ni focus — un clic
          n'importe où la referme, comme avant. */}
      <Fenetre titre={titreCourtDe(couranteBulle)} icone={couranteBulle.icone} className="fenetre-bulle">
        <p className="decouverte-bulle-chapeau">{couranteBulle.chapeau}</p>
        <p className="decouverte-bulle-retenir">{aRetenirCourtDe(couranteBulle)}</p>
        <p className="decouverte-bulle-pied">Un clic ferme.</p>
      </Fenetre>
    </aside>
  );
}
